<template>
  <div class="waste-visualization">
    <div class="space-y-4">
      <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
        <h2 class="text-xl font-bold mb-2">♻️ Mesure du Gaspillage</h2>
        <p class="text-sm opacity-90">Saisie du poids par denrée alimentaire</p>
      </div>

      <!-- Poids par catégorie -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3">⚖️ Poids gaspillé par denrée (kg)</h3>
        <div class="space-y-3">
          <div
            v-for="cat in wasteCategories"
            :key="cat.id"
            class="border-2 rounded-lg p-3"
            :class="cat.weightKg > 0 ? 'border-orange-300 bg-orange-50' : 'border-gray-200'"
          >
            <div class="flex items-center justify-between gap-4">
              <span class="font-semibold text-gray-700 flex-1">{{ cat.icon }} {{ cat.name }}</span>
              <div class="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  v-model.number="cat.weightKg"
                  class="weight-input"
                  :class="cat.weightKg > 0 ? 'weight-input--active' : ''"
                  placeholder="0.00"
                />
                <span class="text-sm text-gray-500">kg</span>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4 bg-red-50 rounded-lg p-4 text-center border-2 border-red-300">
          <p class="text-sm text-gray-600 mb-1">Total gaspillé</p>
          <p class="text-4xl font-bold text-red-600">{{ totalWaste }} kg</p>
        </div>
      </div>

      <!-- Photos des poubelles -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3">📸 Photos des poubelles</h3>
        <div class="flex gap-2 flex-wrap">
          <div
            v-for="(photo, idx) in photos"
            :key="idx"
            class="relative w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center"
          >
            <span class="text-3xl">🗑️</span>
            <button
              @click="removePhoto(idx)"
              class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
            >
              ✕
            </button>
          </div>
          <button
            @click="addPhoto"
            class="w-24 h-24 bg-blue-50 border-2 border-blue-300 border-dashed rounded-lg flex items-center justify-center hover:bg-blue-100"
          >
            <span class="text-3xl text-blue-500">📷</span>
          </button>
        </div>
      </div>

      <!-- Boutons d'action -->
      <div class="grid grid-cols-2 gap-3">
        <button
          @click="emitResult('display')"
          :disabled="parseFloat(totalWaste) === 0"
          :class="parseFloat(totalWaste) > 0 ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-300 cursor-not-allowed'"
          class="text-white rounded-lg p-4 font-semibold shadow-md transition"
        >
          👀 Voir le résultat
        </button>
        <button
          @click="emitResult('poster')"
          :disabled="parseFloat(totalWaste) === 0"
          :class="parseFloat(totalWaste) > 0 ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 cursor-not-allowed'"
          class="text-white rounded-lg p-4 font-semibold shadow-md transition"
        >
          🖨️ Préparer affiche
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WasteVisualizationMedium",
  props: {
    // Catégories pré-remplies lors de l'édition d'une mesure du jour déjà saisie (CT-02-03).
    // Format attendu : [{ id, weightKg }] (le reste — name/icon — provient du barème local).
    initialCategories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const categories = [
      { id: 1, name: "Pain", icon: "🥖", weightKg: 0 },
      { id: 2, name: "Légumes", icon: "🥗", weightKg: 0 },
      { id: 3, name: "Viande/Poisson", icon: "🍖", weightKg: 0 },
      { id: 4, name: "Féculents", icon: "🍚", weightKg: 0 },
      { id: 5, name: "Desserts", icon: "🍮", weightKg: 0 },
      { id: 6, name: "Fruits", icon: "🍎", weightKg: 0 },
    ]
    // Pré-remplissage : on reporte le poids enregistré sur la catégorie correspondante.
    this.initialCategories.forEach((init) => {
      const cat = categories.find((c) => c.id === init.id)
      if (cat) cat.weightKg = parseFloat(init.weightKg) || 0
    })
    return {
      wasteCategories: categories,
      photos: [],
    }
  },
  computed: {
    totalWaste() {
      return this.wasteCategories.reduce((acc, cat) => acc + (parseFloat(cat.weightKg) || 0), 0).toFixed(2)
    },
  },
  methods: {
    addPhoto() {
      this.photos.push({})
    },
    removePhoto(idx) {
      this.photos.splice(idx, 1)
    },
    emitResult(displayMode) {
      this.$emit("waste-saved", {
        type: "medium",
        displayMode,
        categories: this.wasteCategories.map((c) => ({
          id: c.id,
          name: c.name,
          icon: c.icon,
          weight_kg: parseFloat(c.weightKg) || 0,
        })),
        total_weight_kg: parseFloat(this.totalWaste),
      })
    },
  },
}
</script>

<style scoped>
.weight-input {
  width: 6rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.4rem 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  text-align: right;
  transition: border-color 0.15s;
}
.weight-input:focus {
  outline: none;
  border-color: #3b82f6;
}
.weight-input--active {
  border-color: #fdba74;
  background-color: #fff7ed;
  color: #ea580c;
}
.space-y-4 > * + * {
  margin-top: 1rem;
}
.space-y-3 > * + * {
  margin-top: 0.75rem;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 0.75rem;
}
.gap-4 {
  gap: 1rem;
}
.mt-4 {
  margin-top: 1rem;
}
.mb-1 {
  margin-bottom: 0.25rem;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.mb-3 {
  margin-bottom: 0.75rem;
}
.grid {
  display: grid;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.flex {
  display: flex;
}
.flex-1 {
  flex: 1 1 0%;
}
.flex-wrap {
  flex-wrap: wrap;
}
.items-center {
  align-items: center;
}
.justify-between {
  justify-content: space-between;
}
.relative {
  position: relative;
}
.absolute {
  position: absolute;
}
.-top-2 {
  top: -0.5rem;
}
.-right-2 {
  right: -0.5rem;
}
.bg-gradient-to-br {
  background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
}
.from-green-500 {
  --tw-gradient-from: #22c55e;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-green-600 {
  --tw-gradient-to: #16a34a;
}
.rounded-xl {
  border-radius: 0.75rem;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.rounded-full {
  border-radius: 9999px;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
.shadow-md {
  box-shadow: 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.shadow-lg {
  box-shadow: 0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.border-2 {
  border-width: 2px;
}
.border-dashed {
  border-style: dashed;
}
.border-gray-200 {
  border-color: #e5e7eb;
}
.border-orange-300 {
  border-color: #fdba74;
}
.border-blue-300 {
  border-color: #93c5fd;
}
.border-red-300 {
  border-color: #fca5a5;
}
.border-red-500 {
  border-color: #ef4444;
}
.bg-white {
  background-color: #ffffff;
}
.bg-red-50 {
  background-color: #fef2f2;
}
.bg-red-500 {
  background-color: #ef4444;
}
.bg-orange-50 {
  background-color: #fff7ed;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.hover\:bg-blue-100:hover {
  background-color: #dbeafe;
}
.bg-blue-500 {
  background-color: #3b82f6;
}
.hover\:bg-blue-600:hover {
  background-color: #2563eb;
}
.bg-green-500 {
  background-color: #22c55e;
}
.hover\:bg-green-600:hover {
  background-color: #16a34a;
}
.bg-gray-100 {
  background-color: #f3f4f6;
}
.bg-gray-300 {
  background-color: #d1d5db;
}
.text-white {
  color: #ffffff;
}
.text-blue-500 {
  color: #3b82f6;
}
.text-red-600 {
  color: #dc2626;
}
.text-gray-500 {
  color: #6b7280;
}
.text-gray-600 {
  color: #4b5563;
}
.text-gray-700 {
  color: #374151;
}
.text-gray-800 {
  color: #1f2937;
}
.font-bold {
  font-weight: 700;
}
.font-semibold {
  font-weight: 600;
}
.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}
.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}
.text-4xl {
  font-size: 2.25rem;
  line-height: 2.5rem;
}
.p-3 {
  padding: 0.75rem;
}
.p-4 {
  padding: 1rem;
}
.p-6 {
  padding: 1.5rem;
}
.w-full {
  width: 100%;
}
.w-6 {
  width: 1.5rem;
}
.w-24 {
  width: 6rem;
}
.h-6 {
  height: 1.5rem;
}
.h-24 {
  height: 6rem;
}
.opacity-90 {
  opacity: 0.9;
}
.cursor-not-allowed {
  cursor: not-allowed;
}
.transition {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
button {
  cursor: pointer;
  border: none;
  background: none;
}
button:not(:disabled):hover {
  opacity: 0.9;
}
</style>
